import {useEffect, useState} from 'react';
import {useServiceProviderStore} from '../store/serviceProviderStore';
import {formatDate} from '../../../Presentation/utils/dateUtils';
import Spinner from '../../../Presentation/components/ui/Spinner';
import {AppText, AppCardTitle} from '../../../Presentation/components/ui/Typography';
import Modal from '../../../Presentation/components/ui/Modal';
import Button from '../../../Presentation/components/ui/Button';
import AssignBrandForm from './AssignBrandForm';
import UnassignBrandForm from './UnassignBrandForm';
import {Brand} from '../models/serviceProvider.model';
import {ProductAPIDataSource} from '../../products/api';
import {useNotification} from '../../../Services/useNotification';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

interface ServiceProviderDetailsProps {
  providerId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ServiceProviderDetails ({providerId, isOpen, onClose}: ServiceProviderDetailsProps) {
  const {selectedProvider, loading, error, fetchProviderById} = useServiceProviderStore();
  const {error: showError} = useNotification();
  const [showAssignBrandModal, setShowAssignBrandModal] = useState(false);
  const [showUnassignBrandModal, setShowUnassignBrandModal] = useState(false);
  const [availableBrands, setAvailableBrands] = useState<Brand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);

  useEffect(() => {
    if (providerId && isOpen) {
      fetchProviderById(providerId);
      fetchAvailableBrands();
    }
  }, [providerId, isOpen, fetchProviderById]);

  const fetchAvailableBrands = async () => {
    setBrandsLoading(true);
    try {
      const brands = await ProductAPIDataSource.getBrands();

      // Ensure brands is an array
      if (Array.isArray(brands)) {
        setAvailableBrands(brands);
      } else {
        setAvailableBrands([]);
      }
    } catch (error) {
      setAvailableBrands([]);
      StandardizedErrorHandler.getInstance().handleError(error);
    } finally {
      setBrandsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleAssignBrand = () => {
    setShowAssignBrandModal(true);
  };

  const handleAssignBrandSuccess = () => {
    setShowAssignBrandModal(false);
    // Refresh provider details to show updated brands
    if (providerId) {
      fetchProviderById(providerId);
      // Refresh available brands list in case it changed
      fetchAvailableBrands();
    }
  };

  const handleAssignBrandCancel = () => {
    setShowAssignBrandModal(false);
  };

  const handleUnassignBrand = () => {
    setShowUnassignBrandModal(true);
  };

  const handleUnassignBrandSuccess = () => {
    setShowUnassignBrandModal(false);
    // Refresh provider details to show updated brands
    if (providerId) {
      fetchProviderById(providerId);
    }
  };

  const handleUnassignBrandCancel = () => {
    setShowUnassignBrandModal(false);
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        title="تفاصيل مزود الخدمة"
        width="max-w-4xl"
      >
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : !selectedProvider ? (
          // Error is handled by StandardizedErrorHandler and shown as toast notification
          <div className="text-center py-10">
            <p className="text-gray-500">لم يتم العثور على مزود الخدمة</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* معلومات مزود الخدمة */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <AppCardTitle className="text-gray-900">
                  معلومات مزود الخدمة
                </AppCardTitle>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  selectedProvider.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  <AppText>{selectedProvider.statusText}</AppText>
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-4">
                    <AppText>المعلومات الأساسية</AppText>
                  </h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        <AppText>الاسم الكامل</AppText>
                      </dt>
                      <dd className="text-sm text-gray-900">
                        <AppText>{selectedProvider.fullName}</AppText>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        <AppText>اسم المستخدم</AppText>
                      </dt>
                      <dd className="text-sm text-gray-900">
                        <AppText>@{selectedProvider.username}</AppText>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        <AppText>البريد الإلكتروني</AppText>
                      </dt>
                      <dd className="text-sm text-gray-900">
                        <AppText>{selectedProvider.email}</AppText>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        <AppText>رقم الهاتف</AppText>
                      </dt>
                      <dd className="text-sm text-gray-900">
                        <AppText>{selectedProvider.phoneNumber}</AppText>
                      </dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-4">
                    <AppText>معلومات الشركة</AppText>
                  </h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        <AppText>اسم الشركة</AppText>
                      </dt>
                      <dd className="text-sm text-gray-900">
                        <AppText>{selectedProvider.companyName}</AppText>
                      </dd>
                    </div>
                    {selectedProvider.website && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          <AppText>الموقع الإلكتروني</AppText>
                        </dt>
                        <dd className="text-sm text-gray-900">
                          <a href={selectedProvider.website} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-800">
                            <AppText>{selectedProvider.website}</AppText>
                          </a>
                        </dd>
                      </div>
                    )}
                    {selectedProvider.description && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          <AppText>الوصف</AppText>
                        </dt>
                        <dd className="text-sm text-gray-900">
                          <AppText>{selectedProvider.description}</AppText>
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        <AppText>تاريخ الإنشاء</AppText>
                      </dt>
                      <dd className="text-sm text-gray-900">
                        <AppText>{selectedProvider.createdAt ? formatDate(selectedProvider.createdAt.toISOString()) : '-'}</AppText>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>

            {/* البراندات المرتبطة */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-md font-medium text-gray-900">
                  <AppText>البراندات المرتبطة</AppText>
                </h3>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-brand-100 text-brand-800">
                    <AppText>{selectedProvider.brandsCount || 0} براند</AppText>
                  </span>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleAssignBrand}
                      size="small"
                      variant="outline"
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      }
                    >
                      ربط براند
                    </Button>
                    {selectedProvider.brands && selectedProvider.brands.length > 0 && (
                      <Button
                        onClick={handleUnassignBrand}
                        size="small"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        icon={
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        }
                      >
                        إلغاء ربط
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {selectedProvider.brands && selectedProvider.brands.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedProvider.brands.map((brand) => (
                    <div key={brand.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">
                            <AppText>{brand.name}</AppText>
                          </h4>
                          {brand.assignedAt && (
                            <p className="text-xs text-gray-500 mt-1">
                              تم الربط في: {formatDate(brand.assignedAt?.toISOString() || '')}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            مرتبط
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    لا توجد براندات مرتبطة
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    لم يتم ربط أي براندات بهذا مزود الخدمة بعد.
                  </p>
                  <div className="mt-4">
                    <Button
                      onClick={handleAssignBrand}
                      size="small"
                      variant="outline"
                    >
                      ربط براند جديد
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Assign Brand Modal */}
      <Modal
        open={showAssignBrandModal}
        onClose={handleAssignBrandCancel}
        title="ربط براند بمزود الخدمة"
        width="max-w-2xl"
      >
        {brandsLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <AssignBrandForm
            providerId={parseInt(providerId || '0')}
            availableBrands={availableBrands}
            assignedBrands={selectedProvider?.brands || []}
            onSuccess={handleAssignBrandSuccess}
            onCancel={handleAssignBrandCancel}
          />
        )}
      </Modal>

      {/* Unassign Brand Modal */}
      <Modal
        open={showUnassignBrandModal}
        onClose={handleUnassignBrandCancel}
        title="إلغاء ربط براند من مزود الخدمة"
        width="max-w-2xl"
      >
        <UnassignBrandForm
          providerId={parseInt(providerId || '0')}
          providerName={selectedProvider?.fullName || ''}
          assignedBrands={selectedProvider?.brands || []}
          onSuccess={handleUnassignBrandSuccess}
          onCancel={handleUnassignBrandCancel}
        />
      </Modal>
    </>
  );
}
